import { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from '@tiptap/react';

interface UnifiedAutosaveOptions {
  // TipTap editor instance (required)
  editor: Editor | null;
  // Save function to call
  onSave: (content: string) => Promise<boolean | void>;
  // Debounce delay in ms
  debounceMs?: number;
  // Enable/disable auto-save
  enabled?: boolean;
  // Custom error handler
  onError?: (error: unknown) => void;
}

type SaveState = 'upToDate' | 'saving';

export function useUnifiedAutosave({
  editor,
  onSave,
  debounceMs = 2000,
  enabled = true,
  onError,
}: UnifiedAutosaveOptions) {
  const [saveState, setSaveState] = useState<SaveState>('upToDate');
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const activePromiseRef = useRef<Promise<any> | null>(null);
  
  // Simple save function with unmount protection
  const performSave = useCallback(async () => {
    // Check if component is still mounted before starting save
    if (!isMountedRef.current) {
      console.log('[UnifiedAutosave] Save aborted - component unmounted');
      return;
    }
    
    if (!enabled || !editor) {
      console.log('[UnifiedAutosave] Save skipped - not enabled or no editor');
      if (isMountedRef.current) {
        setSaveState('upToDate');
      }
      return;
    }
    
    const contentToSave = editor.getHTML();
    if (!contentToSave) {
      console.log('[UnifiedAutosave] Save skipped - no content');
      if (isMountedRef.current) {
        setSaveState('upToDate');
      }
      return;
    }
    
    console.log('[UnifiedAutosave] Executing save...', {
      contentLength: contentToSave.length,
      timestamp: new Date().toISOString(),
      isMounted: isMountedRef.current
    });
    
    try {
      // Store the active promise and check mount status before and after save
      const savePromise = onSave(contentToSave);
      activePromiseRef.current = savePromise;
      
      await savePromise;
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setSaveState('upToDate');
        console.log('[UnifiedAutosave] Save completed successfully');
      } else {
        console.log('[UnifiedAutosave] Save completed but component unmounted - skipping state update');
      }
    } catch (error) {
      // Only log and handle error if component is still mounted
      if (isMountedRef.current) {
        console.error('[UnifiedAutosave] Save failed:', error);
        setSaveState('upToDate');
        
        if (onError) {
          onError(error);
        }
      } else {
        // Special logging for the pageId UUID error case
        const errorMsg = error?.message || error;
        if (errorMsg.includes && errorMsg.includes('uuid')) {
          console.log('[UnifiedAutosave] Save failed with UUID error after unmount - this is expected and harmless:', errorMsg);
        } else {
          console.log('[UnifiedAutosave] Save failed but component unmounted - ignoring error:', errorMsg);
        }
      }
    } finally {
      activePromiseRef.current = null;
    }
  }, [enabled, editor, onSave, onError]);
  
  // Trigger save with immediate state change and mount check
  const triggerSave = useCallback(() => {
    // Don't trigger save if component is unmounted
    if (!isMountedRef.current) {
      console.log('[UnifiedAutosave] Save trigger ignored - component unmounted');
      return;
    }
    
    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // Show saving state immediately (only if mounted)
    if (isMountedRef.current) {
      setSaveState('saving');
      console.log('[UnifiedAutosave] Content changed - showing saving state');
    }
    
    // Set timer for actual save
    saveTimerRef.current = setTimeout(() => {
      performSave();
    }, debounceMs);
  }, [performSave, debounceMs]);
  
  // Track content changes from editor
  useEffect(() => {
    if (!enabled || !editor) return;
    
    const handleUpdate = () => {
      console.log('[UnifiedAutosave] Editor content changed, triggering save');
      triggerSave();
    };
    
    editor.on('update', handleUpdate);
    
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, enabled, triggerSave]);
  
  // Cleanup on unmount with proper mount tracking
  useEffect(() => {
    // Set mounted to true on mount
    isMountedRef.current = true;
    
    return () => {
      // Mark as unmounted immediately
      isMountedRef.current = false;
      
      // Clear any pending save timer
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        console.log('[UnifiedAutosave] Cleared pending save timer on unmount');
      }
      
      // Log if there's an active save in progress
      if (activePromiseRef.current) {
        console.log('[UnifiedAutosave] Component unmounting with active save in progress - will ignore results');
      }
    };
  }, []);
  
  return {
    isSaving: saveState === 'saving',
    hasError: false,
    saveState,
  };
} 